import { t } from "../i18n";
import { create } from "zustand";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { useProjectStore } from "../state/projectStore";
import { Dialog } from "../ui/Dialog";

/**
 * Ücretsiz katman (Cem 2026-09-23): hesap açık ama yalnız kontrol sonucu (GK + sağlıyor/sağlamıyor) görünür;
 * hesap ayrıntısı (kuvvet tablosu, taban gerilmesi, donatı, metraj), rapor, çıktılar (PDF/Word/DXF/XLSX/CSV/yazdır),
 * paylaşım, otomatik boyutlandırma ve yapay zekâ ile okuma aboneliğe dahil. Sunucu `subscription.fullAccess` ile söyler.
 * API'siz statik sürüm ve paylaşım bağlantısı (ücretli kullanıcının paylaştığı) tam erişimlidir.
 */
function computeFull(status: string, fullAccess: boolean | undefined, origin: string | null): boolean {
  if (origin === "shared") return true;
  return !(status === "authed" && fullAccess === false);
}
export function hasFullAccess(): boolean {
  const a = useAuth.getState();
  return computeFull(a.status, a.subscription?.fullAccess, useProjectStore.getState().origin);
}
export function useFullAccess(): boolean {
  const status = useAuth((s) => s.status);
  const fullAccess = useAuth((s) => s.subscription?.fullAccess);
  const origin = useProjectStore((s) => s.origin);
  return computeFull(status, fullAccess, origin);
}

interface PaywallState { what: string | null; open(what: string): void; close(): void }
export const usePaywall = create<PaywallState>((set) => ({ what: null, open: (what) => set({ what }), close: () => set({ what: null }) }));

/** Tam erişim yoksa ödeme duvarını açar ve false döner (çıktı/özellik çağrılarının başında). */
export function requireFullAccess(what: string): boolean {
  if (hasFullAccess()) return true;
  usePaywall.getState().open(what);
  return false;
}

export function PaywallDialog(): JSX.Element | null {
  const { what, close } = usePaywall();
  if (!what) return null;
  return (
    <Dialog open onClose={close} label={t("Aboneliğe dahil")} width={440}
      footer={<><Link to="/planlar" className="cad-btn-primary" onClick={close}>{t("Planları gör")}</Link><button type="button" className="cad-btn-box" onClick={close}>{t("Kapat")}</button></>}>
      <p><strong>{what}</strong> {t("aboneliğe dahildir.")}</p>
      <p>{t("Ücretsiz hesapta duvarınızı çizip kontrollerin sağlanıp sağlanmadığını ve güvenlik sayılarını görürsünüz. Hesap ayrıntıları, rapor, çizim ve tablo çıktıları, paylaşım ve otomatik boyutlandırma için bir plan seçin.")}</p>
    </Dialog>
  );
}

/** Ücretsiz katmanda ayrıntının yerine konan kısa not (palet/panel içi). */
export function LockedNote({ what, className = "" }: { what: string; className?: string }): JSX.Element {
  return (
    <div className={`border border-dashed border-cad-line bg-cad-off px-2 py-1.5 text-[11px] text-cad-ink2 ${className}`} role="note">
      🔒 {what} {t("aboneliğe dahildir.")} <Link to="/planlar" className="underline text-cad-accent">{t("Planları gör")}</Link>
    </div>
  );
}
